// frontend/src/components/SalesInvoiceList.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import {
  FileText, Building, Truck, Warehouse, Calendar,
  DollarSign, CreditCard, Eye, Edit, Trash2,
  Plus, Search, Filter, Download, RefreshCw,
  Loader2, X, ChevronDown, Globe, Package,
  CheckCircle, Clock, XCircle, AlertCircle,
  ShoppingBag, Receipt, Wallet, Smartphone,
  ArrowLeftRight, ChevronLeft, ChevronRight, Users
} from "lucide-react";
import "./SalesInvoiceList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const SalesInvoiceList = () => {
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency, getCurrencySymbol } = useCurrency();
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [stats, setStats] = useState({
    totalInvoices: 0,
    totalGrandTotal: 0,
    totalPaid: 0,
    totalBalance: 0,
    processInvoices: 0,
    completedInvoices: 0,
    paidInvoices: 0,
    partialInvoices: 0,
    notpaidInvoices: 0,
    saleInvoices: 0,
    returnInvoices: 0
  });

  // Filter state
  const [filter, setFilter] = useState({
    status: "",
    payment_status: "",
    customer_id: "",
    start_date: "",
    end_date: "",
    is_return: ""
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  const isCompanyAdmin = userType === 'company' && currentUser?.role === 'admin';

  useEffect(() => {
    fetchInvoices();
    fetchCustomers();

    if (isSystemAdmin) {
      fetchCompanies();
    }
  }, [selectedCompany, currentPage, filter]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);

      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage
      });

      if (filter.status) queryParams.append("status", filter.status);
      if (filter.payment_status) queryParams.append("payment_status", filter.payment_status);
      if (filter.customer_id) queryParams.append("customer_id", filter.customer_id);
      if (filter.start_date) queryParams.append("start_date", filter.start_date);
      if (filter.end_date) queryParams.append("end_date", filter.end_date);
      if (filter.is_return !== "") queryParams.append("is_return", filter.is_return);
      if (isSystemAdmin && selectedCompany) queryParams.append("company_id", selectedCompany);

      const response = await axios.get(`${url}/api/sales/invoices?${queryParams}`);

      if (response.data.success) {
        setInvoices(response.data.data);
        setTotalPages(response.data.totalPages || 1);
        setTotalCount(response.data.totalCount || response.data.data.length);

        if (response.data.stats) {
          setStats(response.data.stats);
        } else {
          calculateLocalStats(response.data.data);
        }
      }
    } catch (error) {
      toast.error("Failed to load sales invoices");
    } finally {
      setLoading(false);
    }
  };

  const calculateLocalStats = async (invoicesData) => {
    const newStats = {
      totalInvoices: invoicesData.length,
      totalGrandTotal: 0,
      totalPaid: 0,
      totalBalance: 0,
      processInvoices: 0,
      completedInvoices: 0,
      paidInvoices: 0,
      partialInvoices: 0,
      notpaidInvoices: 0,
      saleInvoices: 0,
      returnInvoices: 0
    };

    for (const invoice of invoicesData) {
      try {
        const paymentsResponse = await axios.get(`${url}/api/customerpayment?sales_invoice_id=${invoice._id}&status_ne=cancelled`);

        const customerPayments = paymentsResponse.data.success ? paymentsResponse.data.data : [];
        const totalCustomerPaid = customerPayments.reduce((sum, p) => sum + (p.total_paying_now || 0), 0);

        const totalPaidWithCustomers = (invoice.total_amount_paid || 0) + totalCustomerPaid;

        newStats.totalGrandTotal += invoice.grand_total || 0;
        newStats.totalPaid += totalPaidWithCustomers;

        if (invoice.is_return) {
          newStats.returnInvoices += 1;
        } else {
          newStats.saleInvoices += 1;
        }

        if (invoice.status === 'process') newStats.processInvoices += 1;
        else if (invoice.status === 'completed') newStats.completedInvoices += 1;

        if (totalPaidWithCustomers >= (invoice.grand_total || 0)) {
          newStats.paidInvoices += 1;
        } else if (totalPaidWithCustomers > 0) {
          newStats.partialInvoices += 1;
        } else {
          newStats.notpaidInvoices += 1;
        }
      } catch (error) {
        newStats.totalGrandTotal += invoice.grand_total || 0;
        newStats.totalPaid += invoice.total_amount_paid || 0;
      }
    }

    newStats.totalBalance = newStats.totalGrandTotal - newStats.totalPaid;
    setStats(newStats);
  };

  const fetchCustomers = async () => {
    try {
      const params = {};

      if (isSystemAdmin && selectedCompany) {
        params.company_id = selectedCompany;
      }

      const response = await axios.get(`${url}/api/customer/dropdown`, { params });

      if (response.data.success) {
        setCustomers(response.data.data || []);
      }
    } catch (error) {
      // Silent fail
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${url}/api/company`);

      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      // Silent fail
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchInvoices();
  };

  const resetFilters = () => {
    setFilter({
      status: "",
      payment_status: "",
      customer_id: "",
      start_date: "",
      end_date: "",
      is_return: ""
    });
    setSelectedCompany("");
    setSearchTerm("");
    setCurrentPage(1);
    fetchInvoices();
  };

  const handleView = (id) => {
    navigate(`/sales/invoice/view/${id}`);
  };

  const handleEdit = async (invoiceId) => {
    try {
      const loadingToast = toast.loading("Loading transaction details...");

      const response = await axios.get(
        `${url}/api/sales/transactions/by-invoice/${invoiceId}`
      );

      toast.dismiss(loadingToast);

      if (response.data.success && response.data.data && response.data.data.length > 0) {
        const transactionId = response.data.data[0]._id;
        navigate(`/sales/edit/${transactionId}`);
      } else {
        toast.info("This invoice has no editable transactions");
      }
    } catch (error) {
      toast.error("Failed to load transactions for this invoice");
    }
  };

  const handleDelete = async (id, invoiceNo) => {
    if (!window.confirm(`Are you sure you want to cancel invoice "${invoiceNo}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await axios.delete(`${url}/api/sales/invoice/${id}`);

      if (response.data.success) {
        toast.success(`Invoice "${invoiceNo}" cancelled successfully`);
        fetchInvoices();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to cancel invoice");
    }
  };

  const handleCreate = () => {
    navigate("/AddSalesTransaction");
  };

  const handleExport = () => {
    if (invoices.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const symbol = getCurrencySymbol();
      const headers = [
        "Invoice No", "Reference ID", "Customer", "Invoice Date", "Due Date",
        `Grand Total (${symbol})`, `Amount Paid - Sale Form (${symbol})`,
        `Customer Payments (${symbol})`, `Total Paid (${symbol})`,
        `Balance (${symbol})`, "Payment Status", "Status", "Type"
      ];

      const csvData = invoices.map(invoice => {
        const customerTotal = invoice.customer_payments_total || 0;
        const totalPaid = (invoice.total_amount_paid || 0) + customerTotal;
        const balance = (invoice.grand_total || 0) - totalPaid;
        return [
          invoice.invoice_no || "",
          invoice.reference_id || "",
          invoice.customer_id?.customer_name || "N/A",
          new Date(invoice.invoice_date).toLocaleDateString(),
          invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : "N/A",
          invoice.grand_total || 0,
          invoice.total_amount_paid || 0,
          customerTotal,
          totalPaid,
          balance,
          invoice.payment_status || "N/A",
          invoice.status || "N/A",
          invoice.is_return ? "Return" : "Sale"
        ];
      });

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell =>
          typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
        ).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `sales-invoices-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success("Export started successfully");
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return "Invalid date";
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      process: { icon: Clock, bg: '#cce5ff', color: '#004085', label: 'Process' },
      pending: { icon: Clock, bg: '#fff3cd', color: '#856404', label: 'Pending' },
      completed: { icon: CheckCircle, bg: '#d4edda', color: '#155724', label: 'Completed' },
      cancelled: { icon: XCircle, bg: '#f8d7da', color: '#721c24', label: 'Cancelled' },
      returned: { icon: AlertCircle, bg: '#e2d5f1', color: '#4a1c6c', label: 'Returned' }
    };

    const config = statusConfig[status] || statusConfig.process;
    const Icon = config.icon;

    return (
      <span className="status-badge" style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 600,
        backgroundColor: config.bg,
        color: config.color
      }}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: '#fff3cd', color: '#856404', label: 'Pending' },
      paid: { bg: '#d4edda', color: '#155724', label: 'Paid' },
      partial: { bg: '#cce5ff', color: '#004085', label: 'Partial' },
      notpaid: { bg: '#f8d7da', color: '#721c24', label: 'Not Paid' },
      overdue: { bg: '#f8d7da', color: '#721c24', label: 'Overdue' }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className="payment-badge" style={{
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 600,
        backgroundColor: config.bg,
        color: config.color
      }}>
        {config.label}
      </span>
    );
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'cash': return <CreditCard size={12} />;
      case 'evc': return <Smartphone size={12} />;
      case 'edahab': return <Wallet size={12} />;
      case 'bank_transfer': return <Truck size={12} />;
      default: return <DollarSign size={12} />;
    }
  };

  // Filter invoices based on search term
  const filteredInvoices = invoices.filter(invoice => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    const customerName = invoice.customer_id?.customer_name || "";
    const customerCode = invoice.customer_id?.customer_code || "";

    return (
      (invoice.invoice_no && invoice.invoice_no.toLowerCase().includes(searchLower)) ||
      (invoice.reference_id && invoice.reference_id.toLowerCase().includes(searchLower)) ||
      customerName.toLowerCase().includes(searchLower) ||
      customerCode.toLowerCase().includes(searchLower) ||
      (invoice.payment_status && invoice.payment_status.toLowerCase().includes(searchLower)) ||
      (invoice.status && invoice.status.toLowerCase().includes(searchLower))
    );
  });

  // Pagination for filtered results
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentInvoices = filteredInvoices.slice(indexOfFirstItem, indexOfLastItem);
  const filteredTotalPages = Math.ceil(filteredInvoices.length / itemsPerPage);

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="animate-spin" size={48} />
        <p>Loading sales invoices...</p>
      </div>
    );
  }

  return (
    <div className="invoice-list-container">
      {/* Header */}
      <div className="header-section">
        <div className="title-section">
          <Receipt size={28} />
          <div>
            <h1>Sales Invoices</h1>
            <p className="company-info">
              {isSystemAdmin ? (
                <>
                  <Globe size={14} /> All Companies
                  {selectedCompany && companies.find(c => c._id === selectedCompany) && (
                    <> - {companies.find(c => c._id === selectedCompany)?.company_name}</>
                  )}
                </>
              ) : (
                <>
                  <Building size={14} /> {company?.company_name} ({company?.company_code})
                </>
              )}
            </p>
          </div>
        </div>

        <div className="header-actions">
          <button className="filter-toggle" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={16} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button className="export-btn" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
          <button className="refresh-btn" onClick={fetchInvoices}>
            <RefreshCw size={16} />
            Refresh
          </button>
          <button className="create-btn" onClick={handleCreate}>
            <Plus size={16} />
            New Invoice
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">
            <Receipt size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.totalInvoices}</span>
            <span className="stat-label">Total Invoices</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">
            <ShoppingBag size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.saleInvoices}</span>
            <span className="stat-label">Sales</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple">
            <ArrowLeftRight size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.returnInvoices}</span>
            <span className="stat-label">Returns</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon orange">
            <DollarSign size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{formatCurrency(stats.totalGrandTotal)}</span>
            <span className="stat-label">Total Amount</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon teal">
            <Wallet size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{formatCurrency(stats.totalPaid)}</span>
            <span className="stat-label">Total Paid</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon red">
            <AlertCircle size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{formatCurrency(stats.totalBalance)}</span>
            <span className="stat-label">Total Balance</span>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-header">
            <Filter size={18} />
            <span>Filter Invoices</span>
            <button onClick={resetFilters} className="clear-filters">
              Clear All
            </button>
          </div>

          <div className="filters-grid">
            {isSystemAdmin && (
              <div className="filter-group">
                <label>Company</label>
                <select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                >
                  <option value="">All Companies</option>
                  {companies.map(comp => (
                    <option key={comp._id} value={comp._id}>
                      {comp.company_name} ({comp.company_code})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="filter-group">
              <label>Status</label>
              <select name="status" value={filter.status} onChange={handleFilterChange}>
                <option value="">All Status</option>
                <option value="process">Process</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="returned">Returned</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Payment Status</label>
              <select name="payment_status" value={filter.payment_status} onChange={handleFilterChange}>
                <option value="">All Payment Status</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="partial">Partial</option>
                <option value="notpaid">Not Paid</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Invoice Type</label>
              <select name="is_return" value={filter.is_return} onChange={handleFilterChange}>
                <option value="">All Types</option>
                <option value="false">Sale</option>
                <option value="true">Return</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Customer</label>
              <select name="customer_id" value={filter.customer_id} onChange={handleFilterChange}>
                <option value="">All Customers</option>
                {customers.map(customer => (
                  <option key={customer._id} value={customer._id}>
                    {customer.customer_code} - {customer.customer_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                name="start_date"
                value={filter.start_date}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                name="end_date"
                value={filter.end_date}
                onChange={handleFilterChange}
              />
            </div>
          </div>

          <div className="filters-actions">
            <button onClick={applyFilters} className="apply-filters">
              <Filter size={16} /> Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by invoice no, reference, customer, status..."
            value={searchTerm}
            onChange={handleSearch}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm("")}>
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Invoices Table */}
      <div className="table-container">
        <table className="invoices-table">
          <thead>
            <tr>
              <th>Invoice No</th>
              <th>Reference</th>
              <th>Customer</th>
              <th>Date</th>
              <th>Due Date</th>
              <th>Grand Total</th>
              <th>Amount Paid</th>
              <th>Balance</th>
              <th>Payment Status</th>
              <th>Status</th>
              <th>Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentInvoices.length === 0 ? (
              <tr>
                <td colSpan="12" className="no-data">
                  <div className="empty-state">
                    <Receipt size={48} color="#ccc" />
                    <p>No sales invoices found</p>
                    <button onClick={handleCreate} className="create-btn">
                      <Plus size={16} /> Create First Invoice
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              currentInvoices.map(invoice => {
                const customerTotal = invoice.customer_payments_total || 0;
                const totalPaid = invoice.total_paid_with_customers || (invoice.total_amount_paid || 0) + customerTotal;
                const balance = invoice.actual_balance !== undefined
                  ? invoice.actual_balance
                  : (invoice.grand_total || 0) - totalPaid;
                const isFullyPaid = balance <= 0.01;
                const paymentStatus = invoice.payment_status || (isFullyPaid ? "paid" : (totalPaid > 0 ? "partial" : "notpaid"));

                return (
                  <tr key={invoice._id} className={invoice.is_return ? 'return-row' : ''}>
                    <td>
                      <div className="invoice-no">
                        <span className="no">{invoice.invoice_no}</span>
                        {invoice.is_return && <span className="return-badge">RETURN</span>}
                      </div>
                    </td>
                    <td>
                      <span className="reference-id">{invoice.reference_id || '-'}</span>
                    </td>
                    <td>
                      <div className="customer-info">
                        <Users size={14} />
                        <span>{invoice.customer_id?.customer_name || 'N/A'}</span>
                        <small>{invoice.customer_id?.customer_code || ''}</small>
                      </div>
                    </td>
                    <td>{formatDate(invoice.invoice_date)}</td>
                    <td>{formatDate(invoice.due_date)}</td>
                    <td className="amount grand-total">{formatCurrency(invoice.grand_total)}</td>
                    <td className="amount paid">
                      {formatCurrency(totalPaid)}
                      {customerTotal > 0 && (
                        <small style={{ display: 'block', fontSize: '10px', color: '#666' }}>
                          (Inc. {formatCurrency(customerTotal)} from customer payments)
                        </small>
                      )}
                      {invoice.total_amount_paid > 0 && customerTotal > 0 && (
                        <small style={{ display: 'block', fontSize: '9px', color: '#999' }}>
                          Sale form: {formatCurrency(invoice.total_amount_paid)} | Customer: {formatCurrency(customerTotal)}
                        </small>
                      )}
                    </td>
                    <td className="amount balance" style={{
                      color: isFullyPaid ? '#52c41a' : (balance > 0 ? '#ff4d4f' : '#52c41a'),
                      fontWeight: 600
                    }}>
                      {formatCurrency(Math.abs(balance))}
                      {isFullyPaid && <CheckCircle size={12} style={{ marginLeft: '4px', display: 'inline' }} />}
                    </td>
                    <td>{getPaymentStatusBadge(paymentStatus)}</td>
                    <td>{getStatusBadge(invoice.status)}</td>
                    <td>
                      <span className={`type-badge ${invoice.is_return ? 'return' : 'sale'}`}>
                        {invoice.is_return ? 'Return' : 'Sale'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="view-btn"
                          onClick={() => handleView(invoice._id)}
                          title="View Invoice"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="edit-btn"
                          onClick={() => handleEdit(invoice._id)}
                          title="Edit Transaction"
                          disabled={invoice.status === 'cancelled' || invoice.status === 'returned' || invoice.status === 'completed' || isFullyPaid}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(invoice._id, invoice.invoice_no)}
                          title="Cancel Invoice"
                          disabled={invoice.status === 'cancelled' || invoice.status === 'returned' || invoice.status === 'completed'}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {filteredInvoices.length > 0 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              <ChevronLeft size={16} />
              Previous
            </button>

            <div className="page-numbers">
              {Array.from({ length: Math.min(5, filteredTotalPages) }, (_, i) => {
                let pageNum;
                if (filteredTotalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= filteredTotalPages - 2) {
                  pageNum = filteredTotalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, filteredTotalPages))}
              disabled={currentPage === filteredTotalPages}
              className="pagination-btn"
            >
              Next
              <ChevronRight size={16} />
            </button>

            <div className="pagination-info">
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredInvoices.length)} of {filteredInvoices.length} entries
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesInvoiceList;